import { ArrowDown, ArrowUp, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { clampWidth, nextWidth } from './column-resize';
import { DataGridEmpty } from './DataGridEmpty';
import { DataGridError } from './DataGridError';
import { DataGridPagination } from './DataGridPagination';
import { DataGridToolbar } from './DataGridToolbar';
import { useColumnWidths } from './use-column-widths';

import type {
  DataGridBulkAction,
  DataGridColumn,
  DataGridFilter,
  DataGridState,
  Density,
} from './types';

import { cn } from '@/lib/cn';

/** Checkbox supporting an indeterminate (partial) state via ref. */
function SelectCheckbox({
  checked,
  indeterminate,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  label: string;
}): React.JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !checked && !!indeterminate;
  }, [checked, indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      aria-label={label}
      className="accent-primary h-4 w-4 cursor-pointer rounded border-[var(--border-strong)]"
    />
  );
}

export interface DataGridProps<TRow> {
  columns: ReadonlyArray<DataGridColumn<TRow>>;
  data: ReadonlyArray<TRow>;
  pagination: { page: number; pageSize: number; totalItems: number } | null;
  isLoading?: boolean;
  isError?: boolean;
  error?: { title?: string; description?: string };
  onRetry?: () => void;
  /** Hide the whole toolbar (search + filters + view/columns menu + export).
   *  Used by fixed, read-only embeds (e.g. a drill-down dialog). */
  hideToolbar?: boolean;

  state: DataGridState;
  onStateChange: {
    setSearch: (q: string) => void;
    setFilter: (id: string, value: string) => void;
    setSort: (sortBy: string | null, sortDir?: 'asc' | 'desc') => void;
    setPage: (page: number) => void;
    setPageSize: (size: number) => void;
    setDensity: (density: Density) => void;
    setHidden: (hidden: string[]) => void;
  };

  filters?: ReadonlyArray<DataGridFilter>;
  getRowId: (row: TRow, index: number) => string;
  onRowClick?: (row: TRow) => void;

  /** Enable per-row selection (checkbox column + select-all + bulk action bar). */
  selectable?: boolean;
  /** Actions shown in the bulk bar when ≥1 row is selected. Requires `selectable`. */
  bulkActions?: ReadonlyArray<DataGridBulkAction<TRow>>;

  searchPlaceholder?: string;
  pageSizeOptions?: ReadonlyArray<number>;
  emptyTitle?: string;
  emptyDescription?: string;

  toolbarActions?: ReactNode;
  /** Extra controls for the filter row (e.g. a date range picker). */
  filtersExtra?: ReactNode;
  /** Toolbar "Export" button that exports ALL matching rows (page supplies the
   *  fetch-all + format logic). May be async. */
  onExport?: () => void | Promise<void>;
  exportLabel?: string;
  /** Overrides the default ADMIN-only export gate for this grid (finance
   *  listings pass `useCanExportFinanceData()` so FINANCE can export too). */
  canExportOverride?: boolean;
  /** Stable id for persisting column widths in localStorage. Defaults to the route path. */
  gridId?: string;

  /**
   * Table layout strategy.
   *   - `fixed` (default) - columns honour their `width` (or persisted drag
   *     width); cell content word-wraps inside that width. The historical
   *     pattern across every master module.
   *   - `auto` - column widths grow with content; the outer `overflow-x-auto`
   *     then scrolls horizontally so every column reads in full without
   *     truncation. Drag-resize still works: a resized column locks to its
   *     dragged width while siblings stay auto-sized. Use when row content
   *     varies a lot (e.g. Users: long names, sales coverage, hierarchy).
   *
   * Cell text wraps by default in both modes - no ellipsis. Opt a column out
   * with `column.truncate: false` (for interactive controls that paint a
   * stray ellipsis at the clip edge).
   */
  tableLayout?: 'fixed' | 'auto';

  /**
   * Optional expandable-row hook. Return a ReactNode to render an additional
   * `<tr>` (spanning every column) directly beneath the row - use it for
   * master/child expansion (e.g. show older itinerary versions under the
   * latest revision row). Return `null` to skip. The caller owns the
   * expansion state; DataGrid only decides where to paint the extras.
   */
  renderRowExtras?: (row: TRow, index: number) => ReactNode | null;

  /**
   * Optional phone layout for one row. Below the `sm` breakpoint the grid
   * swaps the table for a stacked card per row; by default that card is a
   * generic "label: value" list built from the visible columns. Pass this to
   * paint a purpose-built card instead (reference + status on one line, the
   * amount on the right, and so on). The row container - click target,
   * selection checkbox, hover - is still the grid's; only the inside is yours.
   * Column visibility is not applied to a custom card.
   */
  renderMobileCard?: (row: TRow, index: number) => ReactNode;
}

const SKELETON_ROWS = 8;

export function DataGrid<TRow>(props: DataGridProps<TRow>): React.JSX.Element {
  const {
    columns,
    data,
    pagination,
    isLoading,
    isError,
    error,
    onRetry,
    hideToolbar = false,
    state,
    onStateChange,
    filters = [],
    getRowId,
    onRowClick,
    renderRowExtras,
    renderMobileCard,
    selectable = false,
    bulkActions = [],
    searchPlaceholder,
    pageSizeOptions,
    emptyTitle,
    emptyDescription,
    toolbarActions,
    filtersExtra,
    onExport,
    exportLabel,
    canExportOverride,
    gridId,
    tableLayout = 'fixed',
  } = props;

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.alwaysVisible || !state.hidden.includes(c.id)),
    [columns, state.hidden],
  );

  const resolvedGridId =
    gridId ?? (typeof window !== 'undefined' ? window.location.pathname : 'datagrid');
  const { widths, setWidth } = useColumnWidths(resolvedGridId);
  const [drag, setDrag] = useState<{
    id: string;
    startX: number;
    startWidth: number;
    live: number;
  } | null>(null);

  /**
   * Width to apply to the `<col>` and (in auto mode) to the `<th>`.
   * - fixed: every column honours its width.
   * - auto: only resized / dragged columns get a width; siblings stay
   *   intrinsic. A column-supplied static `col.width` is ignored in auto
   *   so the operator's drag wins.
   */
  const computedWidth = (col: DataGridColumn<TRow>): string | number | undefined => {
    if (drag && drag.id === col.id) return drag.live;
    if (tableLayout === 'auto') return widths[col.id];
    return widths[col.id] ?? col.width;
  };
  const isResizable = (col: DataGridColumn<TRow>): boolean => col.resizable !== false;

  const beginResize = (e: React.PointerEvent<HTMLSpanElement>, col: DataGridColumn<TRow>): void => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.closest('th');
    const startWidth = clampWidth(th?.getBoundingClientRect().width ?? widths[col.id] ?? 160);
    setDrag({ id: col.id, startX: e.clientX, startWidth, live: startWidth });
  };

  // Move + up listeners live on `window` while a drag is active. The handle
  // itself is only 1.5px wide, so attaching listeners to it would lose the
  // pointer the instant the cursor moved off the handle. Window-level
  // listeners are the standard pattern for resize drags.
  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent): void => {
      setDrag((d) => (d ? { ...d, live: nextWidth(d.startWidth, e.clientX - d.startX) } : d));
    };
    const onUp = (): void => {
      setDrag((d) => {
        if (d) setWidth(d.id, d.live);
        return null;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    // Belt-and-braces: a body cursor + user-select lock so the page doesn't
    // flicker the text cursor mid-drag or accidentally select text.
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
    // Only the existence of an active drag matters; `setWidth` is stable.
  }, [drag !== null, setWidth]); // eslint-disable-line react-hooks/exhaustive-deps

  const padCell = state.density === 'compact' ? 'px-3 py-1.5' : 'px-3.5 py-2.5';
  const colSpan = visibleColumns.length + (selectable ? 1 : 0);

  // ---- Row selection (only when `selectable`) -------------------------------
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const rowIds = useMemo(() => data.map((row, i) => getRowId(row, i)), [data, getRowId]);
  const rowIdsKey = rowIds.join(',');

  // Prune selection to ids still present whenever the page's rows change (page
  // nav, filter, or a refetch after delete). Keeps valid picks, drops the rest.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(rowIds);
      const next = new Set([...prev].filter((id) => present.has(id)));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rowIdsKey is the stable serialization of rowIds.
  }, [rowIdsKey]);

  const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedIds.has(id));
  const someSelected = !allSelected && rowIds.some((id) => selectedIds.has(id));

  const toggleAll = (): void => {
    setSelectedIds((prev) => {
      if (rowIds.every((id) => prev.has(id))) {
        const next = new Set(prev);
        rowIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...rowIds]);
    });
  };

  const toggleRow = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedIdList = useMemo(() => [...selectedIds], [selectedIds]);
  const selectedRows = useMemo(
    () => data.filter((row, i) => selectedIds.has(getRowId(row, i))),
    [data, getRowId, selectedIds],
  );

  const handleHeaderClick = (column: DataGridColumn<TRow>): void => {
    if (!column.sortable) return;
    if (state.sortBy === column.id) {
      onStateChange.setSort(column.id, state.sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      onStateChange.setSort(column.id, 'asc');
    }
  };

  const showFooter = !isLoading && !isError && data.length > 0 && pagination !== null;

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-[var(--ds-border)] bg-[var(--bg)]">
      {!hideToolbar && (
        <DataGridToolbar
          columns={columns}
          filters={filters}
          state={state}
          // Suppressed while loading / erroring: a stale count next to a
          // spinner reads as the new one.
          pagination={isLoading || isError ? null : pagination}
          onSearchChange={onStateChange.setSearch}
          onFilterChange={onStateChange.setFilter}
          onDensityChange={onStateChange.setDensity}
          onHiddenChange={onStateChange.setHidden}
          searchPlaceholder={searchPlaceholder}
          toolbarActions={toolbarActions}
          filtersExtra={filtersExtra}
          onExport={onExport}
          exportLabel={exportLabel}
          canExportOverride={canExportOverride}
        />
      )}

      {selectable && selectedIds.size > 0 && (
        <div className="flex items-center gap-3 border-b border-[var(--ds-border)] bg-[var(--accent)] px-3 py-2 text-[14px]">
          <span className="font-medium text-[var(--fg)]">{selectedIds.size} selected</span>
          <div className="flex items-center gap-1.5">
            {bulkActions.map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={() => action.onAction(selectedIdList, selectedRows)}
                className={cn(
                  'inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-2.5 text-[13px] font-medium transition-colors',
                  action.danger
                    ? 'border-[var(--ds-error)]/30 text-[var(--ds-error)] hover:bg-[var(--ds-error)]/10'
                    : 'border-[var(--border-strong)] text-[var(--fg)] hover:bg-[var(--z-100)]',
                )}
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto inline-flex cursor-pointer items-center gap-1 text-[13px] text-[var(--fg-muted)] hover:text-[var(--fg)]"
          >
            <X className="h-3.5 w-3.5" aria-hidden /> Clear
          </button>
        </div>
      )}

      {/* Desktop / tablet: the full data table. Hidden on phones (< sm),
          where the card list below takes over. */}
      <div className="hidden overflow-x-auto sm:block">
        <table
          className={
            'w-full border-separate border-spacing-0 text-[15px] ' +
            (tableLayout === 'auto' ? 'table-auto' : 'table-fixed')
          }
        >
          <colgroup>
            {selectable && <col style={{ width: '44px' }} />}
            {visibleColumns.map((col) => {
              const w = computedWidth(col);
              return <col key={col.id} style={w !== undefined ? { width: w } : undefined} />;
            })}
          </colgroup>
          <thead>
            <tr>
              {selectable && (
                <th className="border-b border-[var(--ds-border)] bg-[var(--z-50)] px-3 py-2 align-middle">
                  <SelectCheckbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    label="Select all rows on this page"
                  />
                </th>
              )}
              {visibleColumns.map((col) => {
                const isSorted = state.sortBy === col.id;
                const w = computedWidth(col);
                // In auto layout, `<col width>` is just a hint - the browser
                // grows the column to fit content. Pin the `<th>` with min +
                // max width so the column locks to the dragged size. Fixed
                // layout already honours width via the colgroup so the
                // override is redundant there.
                const thWidthStyle =
                  tableLayout === 'auto' && w !== undefined
                    ? { width: w, minWidth: w, maxWidth: w }
                    : undefined;
                return (
                  <th
                    key={col.id}
                    aria-sort={
                      isSorted ? (state.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                    }
                    style={thWidthStyle}
                    className={cn(
                      'relative border-b border-[var(--ds-border)] bg-[var(--z-50)] px-3 py-2 text-left align-middle text-[13px] font-medium whitespace-nowrap text-[var(--fg-muted)]',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                    )}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => handleHeaderClick(col)}
                        className={cn(
                          'inline-flex items-center gap-1 transition-colors hover:text-[var(--fg)]',
                          col.align === 'right' && 'flex-row-reverse',
                        )}
                      >
                        <span>{col.header}</span>
                        {isSorted ? (
                          <span className="inline-flex text-[var(--fg-muted)]">
                            {state.sortDir === 'asc' ? (
                              <ArrowUp className="h-3 w-3" aria-hidden />
                            ) : (
                              <ArrowDown className="h-3 w-3" aria-hidden />
                            )}
                          </span>
                        ) : null}
                      </button>
                    ) : (
                      col.header
                    )}
                    {isResizable(col) && (
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`Resize column ${typeof col.header === 'string' ? col.header : col.id}`}
                        onPointerDown={(e) => beginResize(e, col)}
                        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize touch-none select-none hover:bg-[var(--primary)]/40"
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              data.length === 0 &&
              Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  {selectable && (
                    <td className={cn(padCell, 'border-b border-[rgb(228_228_231_/_0.55)]')} />
                  )}
                  {visibleColumns.map((col, ci) => (
                    <td
                      key={col.id}
                      className={cn(padCell, 'border-b border-[rgb(228_228_231_/_0.55)]')}
                    >
                      <span
                        className="block h-3 rounded bg-[var(--skel)]"
                        style={{ width: ci === 0 ? '60%' : '40%' }}
                      />
                    </td>
                  ))}
                </tr>
              ))}

            {isError && (
              <tr>
                <td colSpan={colSpan} className="p-0">
                  <DataGridError
                    title={error?.title}
                    description={error?.description}
                    onRetry={onRetry}
                  />
                </td>
              </tr>
            )}

            {!isLoading && !isError && data.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="p-0">
                  <DataGridEmpty title={emptyTitle} description={emptyDescription} />
                </td>
              </tr>
            )}

            {!isLoading &&
              !isError &&
              data.map((row, rowIndex) => {
                const rid = getRowId(row, rowIndex);
                const extras = renderRowExtras ? renderRowExtras(row, rowIndex) : null;
                const totalCols = visibleColumns.length + (selectable ? 1 : 0);
                return (
                  <React.Fragment key={rid}>
                    <tr
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      style={{ animationDelay: `${Math.min(rowIndex, 8) * 20}ms` }}
                      className={cn(
                        'fade-up transition-colors last:[&>td]:border-b-0',
                        onRowClick && 'cursor-pointer hover:bg-[var(--row-hover)]',
                        selectedIds.has(rid) && 'bg-[var(--accent)]/40',
                      )}
                    >
                      {selectable && (
                        <td
                          className={cn(
                            padCell,
                            'border-b border-[rgb(228_228_231_/_0.55)] align-middle',
                          )}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <SelectCheckbox
                            checked={selectedIds.has(rid)}
                            onChange={() => toggleRow(rid)}
                            label="Select row"
                          />
                        </td>
                      )}
                      {visibleColumns.map((col) => (
                        <td
                          key={col.id}
                          className={cn(
                            padCell,
                            'align-top text-[15px] text-[var(--fg)]',
                            col.truncate === false
                              ? 'overflow-visible'
                              : 'wrap-break-word whitespace-normal',
                            'border-b border-[rgb(228_228_231_/_0.55)]',
                            col.align === 'right' && 'text-right tabular-nums',
                            col.align === 'center' && 'text-center',
                            col.className,
                          )}
                        >
                          {col.cell(row, rowIndex)}
                        </td>
                      ))}
                    </tr>
                    {extras !== null && extras !== undefined && (
                      <tr className="bg-zinc-50/40" onClick={(e) => e.stopPropagation()}>
                        <td
                          colSpan={totalCols}
                          className="border-b border-[rgb(228_228_231_/_0.55)] p-0"
                        >
                          {extras}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Phones (< sm): a stacked card per row instead of a horizontally
          scrolling table. Renders from the same data + visible columns, so it
          always mirrors the table. Each column becomes a "label: value" line,
          reusing the column's own cell renderer. */}
      <div className="sm:hidden">
        {isLoading &&
          data.length === 0 &&
          Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <div
              key={`mc-sk-${i}`}
              className="flex flex-col gap-2 border-b border-[var(--ds-border)] px-4 py-3 last:border-b-0"
            >
              <span className="h-3 w-1/2 rounded bg-[var(--skel)]" />
              <span className="h-3 w-2/3 rounded bg-[var(--skel)]" />
              <span className="h-3 w-1/3 rounded bg-[var(--skel)]" />
            </div>
          ))}

        {isError && (
          <DataGridError title={error?.title} description={error?.description} onRetry={onRetry} />
        )}

        {!isLoading && !isError && data.length === 0 && (
          <DataGridEmpty title={emptyTitle} description={emptyDescription} />
        )}

        {!isLoading &&
          !isError &&
          data.map((row, rowIndex) => {
            const rid = getRowId(row, rowIndex);
            return (
              <div
                key={rid}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={{ animationDelay: `${Math.min(rowIndex, 8) * 20}ms` }}
                className={cn(
                  'fade-up flex flex-col gap-2 border-b border-[var(--ds-border)] px-4 py-3 last:border-b-0',
                  onRowClick && 'cursor-pointer hover:bg-[var(--row-hover)]',
                  selectedIds.has(rid) && 'bg-[var(--accent)]/40',
                )}
              >
                {selectable && (
                  <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <SelectCheckbox
                      checked={selectedIds.has(rid)}
                      onChange={() => toggleRow(rid)}
                      label="Select row"
                    />
                  </div>
                )}
                {renderMobileCard
                  ? renderMobileCard(row, rowIndex)
                  : visibleColumns.map((col) => {
                      const headerText = typeof col.header === 'string' ? col.header.trim() : '';
                      return (
                        <div key={col.id} className="flex items-start gap-3">
                          {headerText ? (
                            <span className="w-24 shrink-0 pt-0.5 text-[11px] font-medium tracking-wide text-[var(--fg-faint)] uppercase">
                              {headerText}
                            </span>
                          ) : null}
                          <div
                            className={cn(
                              'min-w-0 flex-1 text-[14px] wrap-break-word text-[var(--fg)]',
                              !headerText && 'flex justify-end',
                            )}
                          >
                            {col.cell(row, rowIndex)}
                          </div>
                        </div>
                      );
                    })}
              </div>
            );
          })}
      </div>

      {showFooter && (
        <DataGridPagination
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalItems={pagination.totalItems}
          onPageChange={onStateChange.setPage}
          onPageSizeChange={onStateChange.setPageSize}
          pageSizeOptions={pageSizeOptions}
        />
      )}
    </div>
  );
}
